import React from 'react';
import { ChevronRight, Info } from 'lucide-react';
import type { Product } from '../../pages/ProductMatrixNew';
import { sortByAvailability } from '../../utils/tableHelpers';

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface SearchResultsTableProps {
  products: Product[];
  visibleCountries: Country[];
  type: 'hardware' | 'software' | 'level2';
  browseMode: boolean;
  selectedCountry: string | null;
  highlightedProduct: string | null;
  onProductClick: (product: Product, type: string) => void;
}

export const SearchResultsTable: React.FC<SearchResultsTableProps> = ({
  products,
  visibleCountries,
  type,
  browseMode,
  selectedCountry,
  highlightedProduct,
  onProductClick
}) => {
  const columnWidth = visibleCountries.length >= 14 ? '45px' : '60px';
  const sortedProducts = sortByAvailability(products);

  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto">
      <table className="w-full min-w-[600px]" style={{tableLayout: 'fixed'}}>
        <tbody>
          {sortedProducts.map((product, index) => {
            const isHighlighted = highlightedProduct && product.solution === highlightedProduct;
            return (
              <tr
                key={index}
                className={`border-b border-border/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:shadow-sm cursor-pointer group transition-colors ${
                  isHighlighted ? 'bg-yellow-100 dark:bg-yellow-900/30 animate-pulse' : ''
                }`}
                onClick={() => onProductClick(product, type)}
                title={`Click to view ${type === 'hardware' ? 'device' : 'product'} details`}
                data-product-name={product.solution}
              >
                <td className="px-4 py-3" style={{width: '300px'}}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-foreground text-sm leading-tight">
                        {type === 'hardware'
                          ? `${product.hardware_family || ''} ${product.model || ''} ${product.tag || ''}`.trim()
                          : product.solution}
                      </div>
                      {product.notes && (
                        <div title="Additional details available">
                          <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        </div>
                      )}
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-blue-600 text-xs font-medium">
                      <span>View</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </td>
                {browseMode ? (
                  visibleCountries.map(country => {
                    const value = product.countries?.[country.code];
                    return (
                      <td key={country.code} className="text-center px-1 py-2" style={{width: columnWidth}}>
                        {value === true ? (
                          <span className="text-sm text-green-600">✓</span>
                        ) : value === 'Coming Soon' ? (
                          <span className="text-transparent">-</span>
                        ) : (
                          <span className="text-sm text-red-500">✗</span>
                        )}
                      </td>
                    );
                  })
                ) : (
                  selectedCountry && (() => {
                    const value = product.countries?.[selectedCountry];
                    return (
                      <td className="text-center px-1 py-2" style={{width: columnWidth}}>
                        {value === true ? (
                          <span className="text-sm text-green-600">✓</span>
                        ) : value === 'Coming Soon' ? (
                          <span className="text-transparent">-</span>
                        ) : (
                          <span className="text-sm text-red-500">✗</span>
                        )}
                      </td>
                    );
                  })()
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
